// Custom hook for Storage Inventory data fetching and mutations
//
// In local dev (import.meta.env.DEV === true):
//   - If VITE_STORAGE_INVENTORY_LOCAL === 'true', reads/writes go through the
//     dev proxy (npm run proxy).
//   - Otherwise reads from static JSON in public/data/storage-inventory/ with
//     in-memory-only writes (no persistence).
//
// In production (deployed):
//   - All reads/writes go through API Gateway → Lambda (DynamoDB + S3).
//
// Either way the frontend only speaks the /inventory/* HTTP contract, so it is
// agnostic to the backend implementation.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import type {
  ServerAsset,
  ElementalAsset,
  NetworkDeviceAsset,
  AssetCategory,
  Asset,
} from '@/types/storageInventory';
import { generateAssetId } from '@/utils/assetUtils';
import { storageInventoryApi } from '@/lib/storageInventoryClient';

interface UseStorageInventoryReturn {
  servers: ServerAsset[];
  elementals: ElementalAsset[];
  networkDevices: NetworkDeviceAsset[];
  isLoading: boolean;
  error: Error | null;
  addAsset: (category: AssetCategory, assetData: Omit<Asset, 'id'>) => Promise<void>;
  updateAsset: (category: AssetCategory, updatedAsset: Asset) => Promise<void>;
  deleteAsset: (category: AssetCategory, assetId: string) => void;
  exportData: (category: AssetCategory) => void;
  refetch: () => void;
}

const DATA_PATHS: Record<AssetCategory, string> = {
  servers: '/data/storage-inventory/servers.json',
  elementals: '/data/storage-inventory/elementals.json',
  network_devices: '/data/storage-inventory/network_devices.json',
};

/**
 * Whether to use the persistent API backend instead of static JSON.
 * - Production: always true (deployed API).
 * - Dev: true when VITE_STORAGE_INVENTORY_LOCAL === 'true', which means the
 *   local dev proxy (npm run proxy) is serving the /inventory/* contract.
 */
function useLocalBackend(): boolean {
  // In production, always use the API
  if (!import.meta.env.DEV) return true;
  // In dev, use the proxy backend when the local flag is set
  return import.meta.env.VITE_STORAGE_INVENTORY_LOCAL === 'true';
}

async function fetchAssetsFromJson<T>(path: string): Promise<T[]> {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to load data from ${path}: ${response.statusText}`);
  }
  const data = await response.json();
  if (!Array.isArray(data)) {
    throw new Error(`Invalid data format in ${path}: expected an array`);
  }
  return data as T[];
}

/**
 * Triggers a browser download of the JSON data for manual persistence.
 * This is only used when the user explicitly wants to save data to disk.
 */
function downloadJSON(data: unknown[], category: AssetCategory): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${category}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function useStorageInventory(): UseStorageInventoryReturn {
  const queryClient = useQueryClient();
  const [mutationError, setMutationError] = useState<Error | null>(null);
  const backendEnabled = useLocalBackend();

  // Fetch function: uses the proxy backend when enabled, static JSON otherwise
  const fetchCategory = useCallback(
    <T>(category: AssetCategory): Promise<T[]> => {
      if (backendEnabled) {
        return storageInventoryApi.scan(category) as Promise<T[]>;
      }
      return fetchAssetsFromJson<T>(DATA_PATHS[category]);
    },
    [backendEnabled]
  );

  // Fetch servers
  const serversQuery = useQuery({
    queryKey: ['storageInventory', 'servers'],
    queryFn: () => fetchCategory<ServerAsset>('servers'),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  // Fetch elementals
  const elementalsQuery = useQuery({
    queryKey: ['storageInventory', 'elementals'],
    queryFn: () => fetchCategory<ElementalAsset>('elementals'),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  // Fetch network devices
  const networkDevicesQuery = useQuery({
    queryKey: ['storageInventory', 'network_devices'],
    queryFn: () => fetchCategory<NetworkDeviceAsset>('network_devices'),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const isLoading =
    serversQuery.isLoading || elementalsQuery.isLoading || networkDevicesQuery.isLoading;

  const fetchError =
    serversQuery.error || elementalsQuery.error || networkDevicesQuery.error || mutationError;

  /**
   * Generate a unique hostname when the field is empty.
   * Checks all existing hostnames across ALL categories to ensure no duplicates.
   * Format: no_hostname_001, no_hostname_002, etc.
   */
  const generateUniqueHostname = useCallback((): string => {
    // Collect all existing hostnames across all categories
    const allHostnames = new Set<string>();
    const allData = [
      ...(queryClient.getQueryData<Asset[]>(['storageInventory', 'servers']) || []),
      ...(queryClient.getQueryData<Asset[]>(['storageInventory', 'network_devices']) || []),
    ];
    for (const asset of allData) {
      const hostname = 'hostname' in asset ? asset.hostname : undefined;
      if (hostname) allHostnames.add(hostname.toLowerCase());
    }

    // Find the next available number
    let counter = 1;
    while (allHostnames.has(`no_hostname_${String(counter).padStart(3, '0')}`)) {
      counter++;
    }
    return `no_hostname_${String(counter).padStart(3, '0')}`;
  }, [queryClient]);

  /**
   * If the asset has a hostname field and it's empty, auto-fill with a unique placeholder.
   */
  const ensureHostname = useCallback((category: AssetCategory, assetData: Record<string, unknown>): Record<string, unknown> => {
    if (category === 'servers' || category === 'network_devices') {
      const hn = assetData.hostname;
      if (!hn || (typeof hn === 'string' && hn.trim() === '')) {
        return { ...assetData, hostname: generateUniqueHostname() };
      }
    }
    return assetData;
  }, [generateUniqueHostname]);

  // Add a new asset
  const addAsset = useCallback(
    async (category: AssetCategory, assetData: Omit<Asset, 'id'>) => {
      try {
        setMutationError(null);
        const prepared = ensureHostname(category, assetData as Record<string, unknown>);

        if (backendEnabled) {
          // Persist via the configured backend
          const created = await storageInventoryApi.create(category, prepared as Omit<Asset, 'id'>);
          // Optimistic update: add to cache immediately
          const queryKey = ['storageInventory', category];
          const currentData = queryClient.getQueryData<Asset[]>(queryKey) || [];
          queryClient.setQueryData(queryKey, [...currentData, created]);
        } else {
          // In-memory only (local backend disabled)
          const newId = generateAssetId();
          const newAsset = { ...prepared, id: newId } as Asset;
          const queryKey = ['storageInventory', category];
          const currentData = queryClient.getQueryData<Asset[]>(queryKey) || [];
          queryClient.setQueryData(queryKey, [...currentData, newAsset]);
        }
      } catch (err) {
        setMutationError(
          err instanceof Error ? err : new Error('Failed to add asset')
        );
        throw err;
      }
    },
    [queryClient, backendEnabled, ensureHostname]
  );

  // Update an existing asset
  const updateAsset = useCallback(
    async (category: AssetCategory, updatedAsset: Asset) => {
      try {
        setMutationError(null);

        if (backendEnabled) {
          // Persist via the configured backend
          await storageInventoryApi.update(category, updatedAsset);
        }

        // Update cache (works for both modes)
        const queryKey = ['storageInventory', category];
        const currentData = queryClient.getQueryData<Asset[]>(queryKey) || [];
        const updatedData = currentData.map((asset) =>
          asset.id === updatedAsset.id ? updatedAsset : asset
        );
        queryClient.setQueryData(queryKey, updatedData);
      } catch (err) {
        setMutationError(
          err instanceof Error ? err : new Error('Failed to update asset')
        );
        throw err;
      }
    },
    [queryClient, backendEnabled]
  );

  // Delete an asset
  const deleteAsset = useCallback(
    (category: AssetCategory, assetId: string) => {
      if (backendEnabled) {
        // Persist via the configured backend
        storageInventoryApi.delete(category, assetId).then(() => {
          const queryKey = ['storageInventory', category];
          const currentData = queryClient.getQueryData<Asset[]>(queryKey) || [];
          queryClient.setQueryData(queryKey, currentData.filter((a) => a.id !== assetId));
        }).catch((err) => {
          setMutationError(err instanceof Error ? err : new Error('Failed to delete asset'));
        });
      } else {
        // In-memory only
        const queryKey = ['storageInventory', category];
        const currentData = queryClient.getQueryData<Asset[]>(queryKey) || [];
        queryClient.setQueryData(queryKey, currentData.filter((a) => a.id !== assetId));
      }
    },
    [queryClient, backendEnabled]
  );

  // Export current data as a JSON download
  const exportData = useCallback(
    (category: AssetCategory) => {
      const queryKey = ['storageInventory', category];
      const currentData = queryClient.getQueryData<Asset[]>(queryKey) || [];
      downloadJSON(currentData, category);
    },
    [queryClient]
  );

  // Refetch all categories
  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['storageInventory'] });
  }, [queryClient]);

  return {
    servers: serversQuery.data || [],
    elementals: elementalsQuery.data || [],
    networkDevices: networkDevicesQuery.data || [],
    isLoading,
    error: fetchError as Error | null,
    addAsset,
    updateAsset,
    deleteAsset,
    exportData,
    refetch,
  };
}
